/* Cart-Test Drawer · Frontend-Logik (gemerged 2026-05-13)
 * Features:
 *   - Countdown erst ab ≤48h (davor nur "Frühester Versand am …")
 *   - Dynamische Versandkosten aus /cart/shipping_rates.json (DE only, sonst neutraler Hinweis)
 *   - Quantity per Input direkt eintippbar (debounced)
 *   - Trust-Bar responsive: Desktop Icon+Label, Mobile slim mit Tap-Popups
 *   - Auto-Select des nächstmöglichen Termins beim Drawer-Open
 *   - Pickup-Detection via .checkoutMethod.active + MutationObserver
 *   - Pickup-Wording in B1/B2: "Abholung am …" statt "Versand am …"
 *   - Debug-Output hinter DEBUG-Flag
 */
(function () {
  'use strict';

  // Debug-Flag — auf true setzen für Konsole-Diagnose. Production: false.
  const DEBUG = false;
  function dbg(...args) { if (DEBUG) console.log('[CTD]', ...args); }

  const FREE_SHIPPING_THRESHOLD = 18000;
  const LOCALE = 'de-DE';
  const CURRENCY = 'EUR';

  // Countdown erscheint erst ab ≤ COUNTDOWN_THRESHOLD_HOURS bis zum Cutoff
  const COUNTDOWN_THRESHOLD_HOURS = 48;

  // Geo-Detection: data-country auf #CTDRoot (aus Liquid: localization.country.iso_code)
  // Außerhalb von DE wird die Versandkosten-Anzeige neutralisiert
  function getCountry() {
    const el = document.getElementById('CTDRoot');
    return (el && el.dataset.country) ? el.dataset.country.toUpperCase() : 'DE';
  }
  function isGermany() { return getCountry() === 'DE'; }
  // AT-Rollout 07.08.2026: Österreich bekommt gekühlten Express-Versand (22,90 €,
  // keine Versandkostenfrei-Grenze). Achtung: das ist das Einkaufsland, nicht das
  // Lieferland — die Texte tragen ihre Einschränkung deshalb selbst.
  function isAustria() { return getCountry() === 'AT'; }
  const AT_EXPRESS_CENTS = 2290;

  // Default-Adresse für Shipping-Rates-Lookup (Festland, keine Inselzuschläge)
  const RATE_LOOKUP_ZIP = '79286'; // Glottertal, BW
  const RATE_LOOKUP_COUNTRY = 'Germany';
  const RATE_LOOKUP_PROVINCE = 'Baden-Württemberg';

  // Fallback-Preise (falls API fehlschlägt) — ziehen die hardkodierten Werte aus Liquid nach
  const FALLBACK_RATES = {
    standard: { cents: 590, label: 'Standardversand', why: 'Sicher verpackt mit DPD' },
    express: { cents: 1090, label: 'Express-Kühlversand', why: 'Sicher & gekühlt im Öko-Thermopack' }
  };

  // Versand-Logik
  const SHIPPING_WEEKDAYS = [2, 4]; // Di, Do
  const CUTOFF_HOUR = 9; // 9:00 Uhr am Vortag des Versandtags
  // Deutsche Feiertage 2026 (BW) — an diesen Tagen kein Versand
  const HOLIDAYS = [
    '2026-01-01', // Neujahr
    '2026-04-03', // Karfreitag
    '2026-04-06', // Ostermontag
    '2026-05-01', // Tag der Arbeit
    '2026-05-14', // Christi Himmelfahrt
    '2026-05-25', // Pfingstmontag
    '2026-06-04', // Fronleichnam (BW)
    '2026-10-03', // Tag der Deutschen Einheit
    '2026-11-01', // Allerheiligen (BW)
    '2026-12-25', // 1. Weihnachtsfeiertag
    '2026-12-26'  // 2. Weihnachtsfeiertag
  ];

  // ===== State =====
  let cart = null;
  let cooledHandles = new Set();
  let selectedDate = null;
  let countdownInterval = null;
  let zapietObserver = null;
  let zapietDays = null; // sortiertes Array verfügbarer Versandtage aus Zapiet's DOM
  let zapietLastReadAt = 0;
  let shippingRates = null; // { standard: {cents, label, why}, express: {cents, label, why} }
  let ratesFetchInflight = null; // Promise-Guard gegen Doppel-Fetches

  // ===== Info-Tooltip-Inhalte =====
  // Quellen: #0 Knowledge/versand.md (Single Source of Truth)
  // KEINE Stundenangaben zur Kühldauer (Stefan-Feedback feedback_keine_48h_kuehlung)
  const TOOLTIPS = {
    thermopack: {
      icon: '📦',
      title: 'Öko-Thermopack',
      body: 'Isolierverpackung aus recyceltem Papier — kein Styropor. Gekühlt mit selbst eingeschweißtem Eis. Isoliermatten ins Altpapier, Eisbeutel in den Gelben Sack.'
    },
    cool_cooled: {
      icon: '❄️',
      title: 'Sicher & gekühlt',
      body: 'Kühlpflichtige Ware geht ausschließlich per GO! Express Overnight raus — Zustellung am Folgetag vor 13 Uhr im Öko-Thermopack.'
    },
    // Eigene AT-Fassung: dieselbe Aussage ohne die 13-Uhr-Zusage, die es
    // für Österreich nicht gibt. Die deutsche Fassung bleibt unangetastet.
    cool_cooled_at: {
      icon: '❄️',
      title: 'Sicher & gekühlt',
      body: 'Kühlpflichtige Ware geht ausschließlich per GO! Express raus — Zustellung nach Österreich am Tag nach dem Versand, im Laufe des Tages, im Öko-Thermopack.'
    },
    cool_uncooled: {
      icon: '🌿',
      title: 'Ohne Kühlung haltbar',
      body: 'Diese Spezialitäten sind länger haltbar und brauchen keinen Kühlversand — Lieferung in 1–3 Werktagen per DPD Standard.'
    },
    shipping: {
      icon: '🚚',
      title: 'Versandtage Di & Do',
      body: 'Wir versenden dienstags und donnerstags. Bestellschluss: Montag 9 Uhr für Di-Versand, Mittwoch 9 Uhr für Do-Versand. Abholung im Glottertal kostenfrei.'
    },
    digital_email: {
      icon: '📧',
      title: 'Per E-Mail',
      body: 'Du erhältst den Gutschein direkt nach Zahlungseingang per E-Mail. Kein Warten, kein Paket — perfekt für spontane Geschenke.'
    },
    digital_instant: {
      icon: '⚡',
      title: 'Sofort verfügbar',
      body: 'Keine Lieferzeit, kein Versand. Der Gutscheincode steht direkt zum Einlösen bereit — auch am späten Abend oder am Wochenende.'
    },
    digital_gift: {
      icon: '🎁',
      title: 'Geschenkfertig',
      body: 'PDF zum Ausdrucken oder per E-Mail weiterleiten — der Beschenkte löst den Gutschein im Shop ein, wann er möchte.'
    }
  };

  // Selektoren für Zapiet's Calendar (analog zu LP V2 Auto-Date-Flow)
  const ZAPIET_DATE_BUTTON_SEL = '.Zapiet-Calendar__Dates button:not(.Zapiet-Calendar__Date--disabled):not(.Zapiet-Calendar__Date--different-month)';
  const ZAPIET_ALL_BUTTON_SEL = '.Zapiet-Calendar__Dates button';
  const ZAPIET_INPUT_ISO_SEL = '#Zapiet-InputCalendar__shippingDate_ISO';

  // ===== Helpers =====
  function formatMoney(cents) {
    return (cents / 100).toLocaleString(LOCALE, { style: 'currency', currency: CURRENCY });
  }

  function formatGermanDate(date, opts = {}) {
    const o = { weekday: 'long', day: 'numeric', month: 'long', ...opts };
    if (opts.withYear) o.year = 'numeric';
    return date.toLocaleDateString('de-DE', o);
  }

  function toISODate(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  function isHoliday(date) {
    return HOLIDAYS.includes(toISODate(date));
  }

  function isShippingDay(date) {
    return SHIPPING_WEEKDAYS.includes(date.getDay()) && !isHoliday(date);
  }

  function nextShippingDay(after) {
    // Zapiet ist Source of Truth — falls Tage aus dem DOM gelesen wurden, nutze diese
    if (zapietDays && zapietDays.length > 0) {
      const afterTime = new Date(after);
      afterTime.setHours(0, 0, 0, 0);
      for (const d of zapietDays) {
        if (d.getTime() > afterTime.getTime()) return new Date(d);
      }
    }
    // Fallback: eigene Logik (Di/Do + deutsche Feiertage)
    const d = new Date(after);
    d.setHours(0, 0, 0, 0);
    for (let i = 1; i <= 30; i++) {
      const c = new Date(d);
      c.setDate(d.getDate() + i);
      if (isShippingDay(c)) return c;
    }
    return null;
  }

  function nextDeliveryDay(afterShipDate) {
    // GO! Express Overnight: nächster Werktag (Mo-Fr, keine Feiertage)
    const d = new Date(afterShipDate);
    d.setHours(0, 0, 0, 0);
    for (let i = 1; i <= 14; i++) {
      const c = new Date(d);
      c.setDate(d.getDate() + i);
      const day = c.getDay();
      if (day !== 0 && day !== 6 && !isHoliday(c)) return c;
    }
    return null;
  }

  function nextCutoff(now) {
    // Wenn Zapiet-Tage verfügbar: für jeden Versandtag Cutoff = Vortag CUTOFF_HOUR
    if (zapietDays && zapietDays.length > 0) {
      for (const shipDate of zapietDays) {
        const cutoff = new Date(shipDate);
        cutoff.setDate(shipDate.getDate() - 1);
        cutoff.setHours(CUTOFF_HOUR, 0, 0, 0);
        if (cutoff > now) return { cutoff: new Date(cutoff), shipDate: new Date(shipDate) };
      }
      // Keine Zapiet-Tage mehr in der Zukunft mit ungenutztem Cutoff
      // → erster Zapiet-Tag wird angezeigt ohne Countdown (passes-Pfad greift)
      return null;
    }
    // Fallback: eigene Logik
    const today = new Date(now);
    today.setHours(0, 0, 0, 0);
    for (let i = 0; i <= 30; i++) {
      const c = new Date(today);
      c.setDate(today.getDate() + i);
      c.setHours(CUTOFF_HOUR, 0, 0, 0);
      const nextDay = new Date(c);
      nextDay.setDate(c.getDate() + 1);
      if (isShippingDay(nextDay) && c > now) return { cutoff: c, shipDate: nextDay };
    }
    return null;
  }

  // ===== Zapiet-DOM-Reader =====
  // Liest die verfügbaren Versandtage aus Zapiet's gerendertem Calendar.
  // Robust gegen verschiedene Date-Encoding-Strategien von Zapiet (data-iso, aria-label, etc.).
  function parseDateFromZapietButton(btn) {
    // Strategie 1: data-iso oder data-date Attribut
    for (const attr of ['data-iso', 'data-date', 'data-value']) {
      const v = btn.getAttribute(attr);
      if (v) {
        const m = v.match(/(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
        if (m) {
          const d = new Date(parseInt(m[1]), parseInt(m[2]) - 1, parseInt(m[3]));
          if (!isNaN(d.getTime())) return d;
        }
      }
    }
    // Strategie 2: aria-label mit Datum (z.B. "Dienstag, 19. Mai 2026")
    const aria = btn.getAttribute('aria-label') || btn.getAttribute('title') || '';
    if (aria) {
      // Versuche deutsches Datum
      const monthMap = {
        'januar': 0, 'februar': 1, 'märz': 2, 'maerz': 2, 'april': 3, 'mai': 4, 'juni': 5,
        'juli': 6, 'august': 7, 'september': 8, 'oktober': 9, 'november': 10, 'dezember': 11
      };
      const m = aria.toLowerCase().match(/(\d{1,2})\.?\s+([a-zäö]+)\s+(\d{4})/);
      if (m && monthMap[m[2]] !== undefined) {
        const d = new Date(parseInt(m[3]), monthMap[m[2]], parseInt(m[1]));
        if (!isNaN(d.getTime())) return d;
      }
      // Versuche ISO im aria-label
      const iso = aria.match(/(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
      if (iso) {
        const d = new Date(parseInt(iso[1]), parseInt(iso[2]) - 1, parseInt(iso[3]));
        if (!isNaN(d.getTime())) return d;
      }
    }
    return null;
  }

  function readZapietAvailableDays() {
    const btns = document.querySelectorAll(ZAPIET_DATE_BUTTON_SEL);
    if (btns.length === 0) return null;
    const dates = [];
    btns.forEach(btn => {
      const d = parseDateFromZapietButton(btn);
      if (d) {
        // Nur Tage in der Zukunft (inkl. heute) sammeln
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (d.getTime() >= today.getTime()) dates.push(d);
      }
    });
    if (dates.length === 0) return null;
    dates.sort((a, b) => a.getTime() - b.getTime());
    // Duplikate raus
    const unique = [];
    let lastTime = -1;
    for (const d of dates) {
      const t = d.getTime();
      if (t !== lastTime) { unique.push(d); lastTime = t; }
    }
    return unique;
  }

  function refreshZapietDays() {
    // Drosseln: nicht öfter als alle 250ms
    const now = Date.now();
    if (now - zapietLastReadAt < 250) return;
    zapietLastReadAt = now;
    const days = readZapietAvailableDays();
    if (days && days.length > 0) {
      // Nur updaten wenn sich was geändert hat
      const fingerprint = days.map(d => d.getTime()).join(',');
      const oldFingerprint = zapietDays ? zapietDays.map(d => d.getTime()).join(',') : '';
      if (fingerprint !== oldFingerprint) {
        zapietDays = days;
        dbg('Zapiet-Tage aus DOM geladen:', days.map(d => toISODate(d)));
        renderShipInfo();
      }
      // Auto-Select: nächstmöglichen Termin wählen wenn noch keiner gesetzt ist
      maybeAutoSelectFirstDay();
    }
  }

  // ===== Zapiet-Prime (Methode klicken + Kalender aktiv öffnen) =====
  // Kern-Fix: Zapiet rendert die Datums-Buttons (.Zapiet-Calendar__Dates button)
  // ERST in den DOM, wenn der Kalender aktiv geöffnet ist. Der alte Code hat nur
  // passiv beobachtet → Buttons existierten nie → zapietDays leer + kein Auto-Click.
  // Sequenz portiert aus dem bewährten V2-LP-Flow (theme.lp-landing-v2.liquid).
  let zapietMethodPrimed = false;
  let zapietCalendarPrimed = false;

  function zapietRoot() { return document.getElementById('storePickupApp'); }

  // Schritt 1: Versand-Methode klicken (sonst kann Zapiet auf Abholung stehen
  // oder den Kalender gar nicht zeigen).
  function ctdClickShippingMethod() {
    const root = zapietRoot();
    if (!root) return false;
    const methods = root.querySelectorAll('.checkoutMethod, .checkoutMethodContainer');
    for (const mEl of methods) {
      const t = (mEl.textContent || '').toLowerCase().trim();
      if (t.indexOf('versand') > -1 || t.indexOf('shipping') > -1) { mEl.click(); return true; }
    }
    const cands = root.querySelectorAll('button, [role="button"], [role="tab"], label, a, div[class*="method"], div[class*="Method"], span[class*="method"]');
    for (const el of cands) {
      const t = (el.textContent || '').toLowerCase().trim();
      if (t.indexOf('versand') > -1 || t.indexOf('shipping') > -1 || t.indexOf('delivery') > -1 || t.indexOf('lieferung') > -1) { el.click(); return true; }
    }
    if (methods.length === 1) { methods[0].click(); return true; }
    return false;
  }

  // Schritt 2: Zapiets Kalender aktiv öffnen — Trigger klicken UND --open-Klasse
  // als Fallback hart setzen, damit das Datums-Grid garantiert in den DOM kommt.
  function ctdOpenZapietCalendar() {
    const root = zapietRoot();
    if (!root) return false;
    if (root.querySelector('.Zapiet-Calendar__Wrapper--open')) return true;
    const triggers = root.querySelectorAll('.datePicker, .datepickerIcon, input[class*="datePicker"], input[class*="date"], [class*="datePicker"], .Zapiet-Calendar__Wrapper, .pickadate');
    triggers.forEach(tr => { tr.click(); if (tr.focus) tr.focus(); });
    const wrapper = root.querySelector('.Zapiet-Calendar__Wrapper');
    if (wrapper && !wrapper.classList.contains('Zapiet-Calendar__Wrapper--open')) {
      wrapper.classList.add('Zapiet-Calendar__Wrapper--open');
      return true;
    }
    return !!root.querySelector('.Zapiet-Calendar__Wrapper--open');
  }

  // Treibt die Sequenz idempotent voran (aus dem Polling-Loop aufgerufen):
  // Methode → Kalender öffnen. Nicht bei Pickup/Digital/leer/Datum-gesetzt.
  function primeZapietForAutoDate() {
    if (selectedDate) return;
    if (!cart || cart.item_count === 0) return;
    if (cartIsAllDigital()) return;
    if (isPickupMode()) return; // User hat bewusst Abholung gewählt — nicht eingreifen
    if (!zapietRoot()) return;
    if (!zapietMethodPrimed) {
      zapietMethodPrimed = ctdClickShippingMethod();
      return; // einen Tick warten, dann Kalender
    }
    if (!zapietCalendarPrimed) {
      zapietCalendarPrimed = ctdOpenZapietCalendar();
    }
  }

  // ===== Auto-Date-Selection =====
  // Klickt den ersten verfügbaren Zapiet-Calendar-Button, damit der Kunde
  // schon einen Standard-Termin im Cart hat. Retry-fähig: erst gelatcht, wenn
  // tatsächlich ein selectedDate gesetzt wurde (oder Versuche erschöpft).
  let autoSelectTries = 0;
  const AUTO_SELECT_MAX_TRIES = 8;
  function maybeAutoSelectFirstDay() {
    if (selectedDate) return;
    if (autoSelectTries >= AUTO_SELECT_MAX_TRIES) return;
    if (!cart || cart.item_count === 0) return;
    if (isPickupMode()) return;
    if (cartIsAllDigital()) return; // virtuelle Produkte brauchen kein Versanddatum
    const firstBtn = document.querySelector(ZAPIET_DATE_BUTTON_SEL);
    if (!firstBtn) return; // Kalender noch nicht offen — Prime-Loop versucht es weiter
    autoSelectTries++;
    dbg('Auto-Select: klicke ersten verfügbaren Termin:', firstBtn.getAttribute('aria-label') || firstBtn.textContent.trim());
    firstBtn.click();
    // Cart kurz danach neu fetchen damit selectedDate aus den Attributes greift
    setTimeout(() => fetchCart(), 700);
  }

  // Reset bei Cart-Wechsel (z.B. neue Session / Cart-Clear)
  function resetAutoSelect() {
    autoSelectTries = 0;
    zapietMethodPrimed = false;
    zapietCalendarPrimed = false;
  }

  function watchZapietCalendar() {
    // Initial nach Zapiet's Start-Event suchen
    document.dispatchEvent(new CustomEvent('zapiet:start'));

    // Mutation Observer auf body — Zapiet kann Calendar an beliebiger Stelle einsetzen
    const observer = new MutationObserver(() => refreshZapietDays());
    observer.observe(document.body, { childList: true, subtree: true, attributes: false });

    // Polling-Loop: zuerst Zapiet aktiv primen (Methode + Kalender öffnen),
    // dann die verfügbaren Tage lesen. Läuft bis ein Datum gesetzt ist
    // oder ~45s vergangen sind (Zapiet kann auf langsamen Geräten träge laden).
    let attempts = 0;
    const interval = setInterval(() => {
      attempts++;
      primeZapietForAutoDate();
      refreshZapietDays();
      if (selectedDate || attempts > 90) {
        clearInterval(interval);
      }
    }, 500);
  }

  function $(sel, root = document) { return root.querySelector(sel); }
  function $$(sel, root = document) { return Array.from(root.querySelectorAll(sel)); }

  // ===== Cart-Fetch =====
  async function fetchCart() {
    try {
      const r = await fetch('/cart.js', { credentials: 'same-origin' });
      cart = await r.json();
      dbg('Cart loaded. Attributes:', cart.attributes, '· Note:', cart.note);
      await enrichCooledInfo();
      extractDateFromCartAttributes();
      // Shipping-Rates parallel ziehen (DE only), Re-Render danach
      fetchShippingRates().then(() => renderShippingHint());
      render();
    } catch (e) {
      console.error('CTD fetchCart error:', e);
    }
  }

  // ===== Shipping Rates =====
  // Holt die echten Versandkosten aus Shopify (DE-Adresse hartkodiert,
  // Inselzuschläge bewusst ignoriert da nur DE-Festland berücksichtigt wird).
  async function fetchShippingRates() {
    if (!isGermany()) { shippingRates = null; return; }
    if (!cart || cart.item_count === 0) { shippingRates = null; return; }
    if (cartIsAllDigital()) { shippingRates = null; return; } // virtuelle Produkte: keine Versandkosten
    if (ratesFetchInflight) return ratesFetchInflight;
    ratesFetchInflight = (async () => {
      try {
        const params = new URLSearchParams();
        params.set('shipping_address[zip]', RATE_LOOKUP_ZIP);
        params.set('shipping_address[country]', RATE_LOOKUP_COUNTRY);
        params.set('shipping_address[province]', RATE_LOOKUP_PROVINCE);
        let res = await fetch('/cart/shipping_rates.json?' + params.toString(), { credentials: 'same-origin' });
        // Shopify kann initial 422 zurückgeben, wenn Rates noch berechnet werden — einmal retry
        if (res.status === 422) {
          await new Promise(r => setTimeout(r, 800));
          res = await fetch('/cart/shipping_rates.json?' + params.toString(), { credentials: 'same-origin' });
        }
        if (!res.ok) { shippingRates = null; return; }
        const data = await res.json();
        const rates = data.shipping_rates || [];
        // Standard = nicht-Express (DPD); Express = enthält "express" oder "go!" oder "kühl"
        const isExpressRate = r => /express|go!|kühl|kuehl|over.?night/i.test((r.name || '') + ' ' + (r.source || '') + ' ' + (r.code || ''));
        const standardRate = rates.find(r => !isExpressRate(r));
        const expressRate = rates.find(r => isExpressRate(r));
        shippingRates = {
          standard: standardRate ? { cents: Math.round(parseFloat(standardRate.price) * 100), label: 'Standardversand', why: 'Sicher verpackt mit DPD' } : null,
          express: expressRate ? { cents: Math.round(parseFloat(expressRate.price) * 100), label: 'Express-Kühlversand', why: 'Sicher & gekühlt im Öko-Thermopack' } : null
        };
        dbg('Shipping-Rates aus Shopify:', shippingRates);
      } catch (err) {
        console.warn('[CTD] Shipping-Rates Fetch fehlgeschlagen, nutze Fallback:', err);
        shippingRates = null;
      } finally {
        ratesFetchInflight = null;
      }
    })();
    return ratesFetchInflight;
  }

  async function enrichCooledInfo() {
    if (!cart || !cart.items) return;
    const todo = cart.items.map(i => i.handle).filter(h => !cooledHandles.has(h + '|checked'));
    for (const handle of todo) {
      try {
        const r = await fetch(`/products/${handle}.js`, { credentials: 'same-origin' });
        if (r.ok) {
          const p = await r.json();
          const tags = (p.tags || []).map(t => String(t).toLowerCase());
          if (tags.includes('cooled')) cooledHandles.add(handle);
          cooledHandles.add(handle + '|checked');
        }
      } catch (e) {
        console.warn('CTD: cooled-check fehlgeschlagen für', handle);
      }
    }
  }

  function cartHasCooled() {
    return cart && cart.items.some(i => cooledHandles.has(i.handle));
  }

  // Digital-Only: alle Items im Cart sind virtuell (Geschenkgutscheine, Downloads etc.)
  // → kein Versand, kein Datum, kein Datepicker, keine Trust-Bar nötig.
  function cartIsAllDigital() {
    if (!cart || !cart.items || cart.items.length === 0) return false;
    return cart.items.every(i => i.requires_shipping === false);
  }

  // Erkennt, ob der Kunde im Zapiet-Toggle auf "Abholung" gewechselt hat.
  // Vereinfacht 2026-05-13: nur die im Live-Cart funktionierende Strategie 3
  // (aktive Method-Buttons im DOM). Strategien 1+2 (Cart-Attribute, Hidden Inputs)
  // greifen bei dieser Zapiet-Version nicht und wurden entfernt.
  function isPickupMode() {
    const activeBtns = document.querySelectorAll(
      '.checkoutMethod.active, .checkoutMethod--selected, .checkoutMethod.selected, ' +
      '.checkoutMethodContainer.active, [aria-pressed="true"][class*="ethod"], ' +
      '[data-method].active, [data-checkout-method="pickup"]'
    );
    for (const btn of activeBtns) {
      const text = (btn.textContent || '').toLowerCase();
      if (text.includes('abholung') || text.includes('pickup') || text.includes('laden')) {
        return true;
      }
    }
    return false;
  }

  function extractDateFromCartAttributes() {
    if (!cart || !cart.attributes) { return; }
    for (const [key, value] of Object.entries(cart.attributes)) {
      if (!value) continue;
      const m = String(value).match(/(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
      if (m) {
        const d = new Date(parseInt(m[1]), parseInt(m[2]) - 1, parseInt(m[3]));
        if (!isNaN(d.getTime())) {
          selectedDate = d;
          return;
        }
      }
    }
  }

  // ===== Render =====
  function render() {
    if (!cart) return;
    renderCount();
    renderItems();
    renderFreeShipping();
    renderShippingHint();
    renderShipInfo();
    renderTrustBar();
    renderZapietVisibility();
    renderNoteVisibility();
    renderTotal();
    renderCheckoutButton();
  }

  // Zapiet-Datepicker bei digital-only Carts ausblenden (kein Versand → kein Termin)
  function renderZapietVisibility() {
    const wrap = $('#CTD-ZapietMount');
    if (!wrap) return;
    wrap.style.display = cartIsAllDigital() ? 'none' : '';
  }

  // Kommentarfeld nur bei nicht-leerem Warenkorb zeigen (analog CTD-ShipInfo/Trust).
  // .ctd-note hat keine CSS-display-Regel → natives hidden-Attribut greift sauber.
  function renderNoteVisibility() {
    const note = $('.ctd-note');
    if (!note) return;
    note.hidden = !cart || cart.item_count === 0;
  }

  function renderCount() {
    const el = $('#CTD-Count');
    if (el) el.textContent = `(${cart.item_count})`;
  }

  function renderItems() {
    const list = $('#CTD-CartItems');
    if (!list) return;
    if (!cart || cart.item_count === 0) {
      list.innerHTML = '<li class="ctd-cart-items__empty">🛒 Dein Warenkorb ist noch leer.</li>';
      return;
    }
    list.innerHTML = '';
    cart.items.forEach((item, idx) => {
      const li = document.createElement('li');
      li.className = 'ctd-cart-item';
      li.dataset.line = (idx + 1).toString();
      const img = item.image
        ? `<img class="ctd-cart-item__img" src="${item.image.replace(/(\.[a-z]+)$/, '_200x$1')}" alt="" loading="lazy" width="64" height="64">`
        : '<div class="ctd-cart-item__img"></div>';
      // Stückpreis nur bei qty > 1 (sonst redundant zu Line-Total) — spart eine Zeile
      const unitPriceHtml = item.quantity > 1
        ? `<span class="ctd-cart-item__unit-price">à ${formatMoney(item.final_price)}</span>`
        : '';
      // Variante nur wenn echt vorhanden (nicht "Default Title")
      const variantTitle = item.variant_title && item.variant_title !== 'Default Title'
        ? item.variant_title : null;
      const variantHtml = variantTitle
        ? `<div class="ctd-cart-item__variant">${variantTitle}</div>`
        : '';
      li.innerHTML = `
        ${img}
        <div class="ctd-cart-item__info">
          <div class="ctd-cart-item__row-top">
            <div class="ctd-cart-item__name-wrap">
              <a href="${item.url}" class="ctd-cart-item__name">${item.product_title}</a>
              ${variantHtml}
            </div>
            <div class="ctd-cart-item__line-price">${formatMoney(item.final_line_price)}</div>
          </div>
          <div class="ctd-cart-item__row-bottom">
            <div class="ctd-qty-pill" data-line="${idx + 1}">
              <button type="button" data-action="minus" aria-label="Weniger">−</button>
              <input type="number" inputmode="numeric" min="0" step="1" data-action="set"
                     value="${item.quantity}" aria-label="Menge" class="ctd-qty-pill__input">
              <button type="button" data-action="plus" aria-label="Mehr">+</button>
            </div>
            ${unitPriceHtml}
            <button type="button" class="ctd-trash-btn" data-action="remove" data-line="${idx + 1}" aria-label="Entfernen">🗑</button>
          </div>
        </div>
      `;
      list.appendChild(li);
    });
    list.querySelectorAll('button[data-action]').forEach(b => b.addEventListener('click', handleQtyClick));
    list.querySelectorAll('input[data-action="set"]').forEach(inp => {
      inp.addEventListener('input', handleQtyInput);
      inp.addEventListener('blur', handleQtyBlur);
      inp.addEventListener('keydown', e => { if (e.key === 'Enter') { e.preventDefault(); inp.blur(); } });
    });
  }

  function renderShippingHint() {
    const el = $('#CTD-ShippingHint');
    if (!el || !cart) return;
    if (cart.item_count === 0) { el.hidden = true; return; }
    el.hidden = false;

    // Digital-Only: kein Versand
    if (cartIsAllDigital()) {
      el.className = 'ctd-shipping-hint ctd-shipping-hint--reached';
      el.innerHTML = '✓ Digital geliefert – kein Versand nötig';
      return;
    }

    // Österreich mit kühlpflichtiger Ware: Express-Kühlversand 22,90 € pauschal,
    // ohne Frei-Grenze. Nur-haltbare AT-Warenkörbe laufen weiter über Zone A und
    // fallen deshalb bewusst in den neutralen Zweig darunter.
    if (isAustria() && cartHasCooled() && !isPickupMode()) {
      el.className = 'ctd-shipping-hint';
      el.innerHTML = `zzgl. ${formatMoney(AT_EXPRESS_CENTS)} Express-Kühlversand nach Österreich
      <span class="ctd-shipping-hint__why">Zustellung am Tag nach dem Versand, im Laufe des Tages</span>`;
      return;
    }

    // Nicht-DE: neutraler Hinweis, keine Preise (Stefan-Entscheidung 2026-05-13)
    if (!isGermany()) {
      el.className = 'ctd-shipping-hint';
      el.innerHTML = 'Versandkosten werden im Checkout berechnet';
      return;
    }

    if (isPickupMode()) {
      el.className = 'ctd-shipping-hint ctd-shipping-hint--reached';
      el.textContent = '✓ Abholung im Laden – kostenfrei';
      return;
    }
    if (cart.total_price >= FREE_SHIPPING_THRESHOLD) {
      el.className = 'ctd-shipping-hint ctd-shipping-hint--reached';
      // Die Aussage trägt ihre Einschränkung selbst: sie ist auch dann wahr, wenn
      // ein DE-Kunde nach Österreich verschickt (Lieferland ist hier nicht bekannt).
      el.textContent = '✓ Versandkostenfrei nach Deutschland';
      return;
    }

    // Dyn. Versandkosten aus shippingRates oder Fallback
    el.className = 'ctd-shipping-hint';
    const hasCooled = cartHasCooled();
    const rate = (hasCooled
      ? (shippingRates && shippingRates.express)
      : (shippingRates && shippingRates.standard)
    ) || (hasCooled ? FALLBACK_RATES.express : FALLBACK_RATES.standard);
    el.innerHTML = `zzgl. ${formatMoney(rate.cents)} ${rate.label}
      <span class="ctd-shipping-hint__why">${rate.why}</span>`;
  }

  function renderFreeShipping() {
    const bar = $('#CTD-FreeShipping');
    const label = $('#CTD-FreeShippingLabel');
    const fill = $('#CTD-FreeShippingFill');
    if (!bar || !label || !fill || !cart) return;
    // Bei leerem Cart, Pickup-Mode oder digital-only: Bar verstecken.
    // Ebenso außerhalb Deutschlands: die 180-€-Grenze gilt dort nicht, die Leiste
    // wäre ein Fortschritt auf ein Ziel, das es nicht gibt (versand.md → Österreich).
    if (cart.item_count === 0 || isPickupMode() || cartIsAllDigital() || !isGermany()) {
      bar.hidden = true;
      return;
    }
    bar.hidden = false;
    const total = cart.total_price;
    if (total >= FREE_SHIPPING_THRESHOLD) {
      bar.classList.add('ctd-free-shipping--reached');
      label.innerHTML = '🎉 <strong>Versandkostenfrei</strong> nach Deutschland!';
      fill.style.width = '100%';
    } else {
      bar.classList.remove('ctd-free-shipping--reached');
      label.innerHTML = `Nur noch <strong>${formatMoney(FREE_SHIPPING_THRESHOLD - total)}</strong> bis zum Gratis-Versand`;
      fill.style.width = (total / FREE_SHIPPING_THRESHOLD * 100).toFixed(1) + '%';
    }
  }

  function renderShipInfo() {
    const root = $('#CTD-ShipInfo');
    if (!root) return;
    if (!cart || cart.item_count === 0) {
      root.hidden = true;
      stopCountdown();
      return;
    }
    root.hidden = false;
    const shipText = $('#CTD-DateText');
    const cutoffRow = $('#CTD-CutoffRow');
    const cutoffText = $('#CTD-CutoffText');
    const cutoffIcon = $('#CTD-CutoffIcon');
    const hasCooled = cartHasCooled();
    const isPickup = isPickupMode();

    // Digital-Only: Versand-Info zu Digital-Hinweis umbauen
    if (cartIsAllDigital()) {
      shipText.innerHTML = `<strong>🎁 Digital geliefert</strong>
        <span class="ctd-ship-info__method">Du bekommst den Gutschein direkt per E-Mail</span>`;
      cutoffRow.style.display = 'none';
      stopCountdown();
      return;
    }
    // Wording-Schalter: bei Pickup wird aus "Versand" → "Abholung"
    const verbDate = isPickup ? 'Abholung am' : 'Versand am';
    const verbEarliest = isPickup ? 'Frühestmögliche Abholung am' : 'Frühester Versand am';

    if (selectedDate) {
      const dateStr = formatGermanDate(selectedDate, { withYear: true });
      let methodLine;
      if (isPickup) {
        methodLine = 'Abholung in Glottertal · 7:00–18:00 Uhr';
      } else if (hasCooled) {
        const delivery = nextDeliveryDay(selectedDate);
        const deliveryDay = delivery
          ? delivery.toLocaleDateString('de-DE', { weekday: 'long' })
          : 'Folgetag';
        // Die 13-Uhr-Zusage gilt nur für Deutschland. Für Österreich sagt
        // versand.md ausdrücklich „im Laufe des Tages" — keine Uhrzeit.
        methodLine = isAustria()
          ? `GO! Express · gekühlt · Lieferung am ${deliveryDay} im Laufe des Tages`
          : `GO! Express · gekühlt · Lieferung am ${deliveryDay} vor 13 Uhr`;
      } else {
        methodLine = 'DPD Standard · Lieferung 1–3 Werktage nach Versand';
      }
      shipText.innerHTML = `${verbDate} <strong>${dateStr}</strong>
        <span class="ctd-ship-info__method">${methodLine}</span>`;
      cutoffRow.style.display = 'none';
      stopCountdown();
    } else {
      // Kein Datum gewählt.
      // TRUST-REGEL: Solange Zapiet seine verfügbaren Tage noch NICHT
      // bestätigt hat (zapietDays leer), zeigen wir KEIN konkretes Datum und
      // KEINEN Countdown. Der hartcodierte Di/Do-Fallback in nextShippingDay/
      // nextCutoff kennt Zapiets echte Vorlaufzeit/Sperrtage nicht und würde
      // sonst ein Lieferversprechen machen, das der Shop evtl. nicht hält
      // (genau der Bug: "Frühester Versand 19. Mai", obwohl Zapiet erst 27.5.).
      const zapietConfirmed = zapietDays && zapietDays.length > 0;
      const now = new Date();

      if (!zapietConfirmed) {
        const fallbackQ = isPickup ? "Wann möchtest du abholen?" : "Wann soll's bei dir sein?";
        shipText.innerHTML = `<strong>${fallbackQ}</strong>
          <span class="ctd-ship-info__method"><a href="#CTD-ZapietMount">Wunschtermin unten auswählen ↓</a></span>`;
        cutoffRow.style.display = 'none';
        stopCountdown();
        return;
      }

      // Ab hier: Zapiet hat Tage bestätigt → Datum & Countdown sind gesichert
      // (nextCutoff/nextShippingDay nutzen dann zapietDays als Source of Truth).
      const next = nextCutoff(now);
      const earliestShipDate = (next && next.cutoff > now) ? next.shipDate : nextShippingDay(now);

      if (earliestShipDate) {
        const dateStr = formatGermanDate(earliestShipDate);
        shipText.innerHTML = `${verbEarliest} <strong>${dateStr}</strong>
          <span class="ctd-ship-info__method"><a href="#CTD-ZapietMount">Wunschtermin unten auswählen ↓</a></span>`;
      } else {
        const fallbackQ = isPickup ? "Wann möchtest du abholen?" : "Wann soll's bei dir sein?";
        shipText.innerHTML = `<strong>${fallbackQ}</strong>
          <span class="ctd-ship-info__method"><a href="#CTD-ZapietMount">Wunschtermin unten auswählen ↓</a></span>`;
      }

      if (next && next.cutoff > now) {
        const hoursToCutoff = (next.cutoff - now) / (1000 * 60 * 60);
        if (hoursToCutoff > COUNTDOWN_THRESHOLD_HOURS) {
          // Vor 48h: kein Countdown — Datum steht schon oben
          cutoffRow.style.display = 'none';
          stopCountdown();
        } else {
          // ≤ 48h: Urgency-Countdown (Wording via verbDate)
          cutoffRow.style.display = 'flex';
          cutoffIcon.textContent = '⏱️';
          cutoffText.classList.remove('passed');
          startCountdown(next.cutoff, next.shipDate, hasCooled, isPickup);
        }
      } else {
        cutoffRow.style.display = 'none';
        stopCountdown();
      }
    }
  }

  function renderTrustBar() {
    const trust = $('#CTD-Trust');
    const trustDigital = $('#CTD-TrustDigital');
    if (!trust) return;
    // Bei leerem Cart: beide Trust-Bars verstecken
    if (!cart || cart.item_count === 0) {
      trust.hidden = true;
      if (trustDigital) trustDigital.hidden = true;
      return;
    }
    // Digital-only: Digital-Trust-Bar zeigen, physische verstecken
    if (cartIsAllDigital()) {
      trust.hidden = true;
      if (trustDigital) trustDigital.hidden = false;
      return;
    }
    // Standard: physische Trust-Bar zeigen, Digital verstecken
    if (trustDigital) trustDigital.hidden = true;
    trust.hidden = false;
    const icon = $('#CTD-Trust2Icon');
    // Slim-Variante hat keinen Label-Slot — Label-Update tolerant via optional chain
    const label = $('#CTD-Trust2Label');
    const hasCooled = cartHasCooled();
    if (hasCooled) {
      trust.classList.add('cooled');
      trust.classList.remove('uncooled');
      if (icon) icon.textContent = '❄️';
      if (label) label.innerHTML = 'Sicher &amp; gekühlt';
      // title-Attribut auf dem Container für Slim-Variante (Tooltip-Erklärung)
      const item2 = icon ? icon.closest('.ctd-trust__item') : null;
      if (item2) item2.setAttribute('title', 'Sicher & gekühlt');
    } else {
      trust.classList.add('uncooled');
      trust.classList.remove('cooled');
      if (icon) icon.textContent = '🌿';
      if (label) label.innerHTML = 'Ohne Kühlung haltbar';
      const item2 = icon ? icon.closest('.ctd-trust__item') : null;
      if (item2) item2.setAttribute('title', 'Ohne Kühlung haltbar');
    }
  }

  function renderTotal() {
    const el = $('#CTD-Total');
    if (el && cart) el.textContent = formatMoney(cart.total_price);
  }

  function renderCheckoutButton() {
    const btn = $('#CTD-Checkout');
    if (btn) btn.disabled = !cart || cart.item_count === 0;
  }

  // ===== Countdown =====
  function startCountdown(cutoff, shipDate, hasCooled, isPickup) {
    stopCountdown();
    const el = $('#CTD-CutoffText');
    if (!el) return;
    const verb = isPickup ? 'Abholbereit am' : 'Versand am';
    function tick() {
      const remaining = cutoff - new Date();
      if (remaining <= 0) { renderShipInfo(); return; }
      const t = Math.floor(remaining / 1000);
      const days = Math.floor(t / 86400);
      const h = Math.floor((t % 86400) / 3600);
      const m = Math.floor((t % 3600) / 60);
      const s = t % 60;
      let durationStr;
      if (days > 0) {
        durationStr = `${days} Tag${days === 1 ? '' : 'e'} ${h} Std ${String(m).padStart(2, '0')} Min`;
      } else {
        durationStr = `${h} Std ${String(m).padStart(2, '0')} Min ${String(s).padStart(2, '0')} Sek`;
      }
      const shipDateStr = formatGermanDate(shipDate);
      el.innerHTML = `Bestellschluss in <strong>${durationStr}</strong> — ${verb} ${shipDateStr}.`;
    }
    tick();
    countdownInterval = setInterval(tick, 1000);
  }
  function stopCountdown() {
    if (countdownInterval) { clearInterval(countdownInterval); countdownInterval = null; }
  }

  // ===== Info-Tooltip =====
  function openTooltip(key) {
    // "cool" ist dynamisch — je nach cooled-Status
    let resolved = key;
    if (key === 'cool') {
      if (!cartHasCooled()) {
        resolved = 'cool_uncooled';
      } else {
        resolved = isAustria() ? 'cool_cooled_at' : 'cool_cooled';
      }
    }
    const t = TOOLTIPS[resolved];
    if (!t) return;
    const root = $('#CTD-Tooltip');
    const iconEl = $('#CTD-TooltipIcon');
    const titleEl = $('#CTD-TooltipTitle');
    const bodyEl = $('#CTD-TooltipBody');
    if (!root || !iconEl || !titleEl || !bodyEl) return;
    iconEl.textContent = t.icon;
    titleEl.textContent = t.title;
    bodyEl.textContent = t.body;
    root.hidden = false;
  }
  function closeTooltip() {
    const root = $('#CTD-Tooltip');
    if (root) root.hidden = true;
  }
  function isTooltipOpen() {
    const root = $('#CTD-Tooltip');
    return root && !root.hidden;
  }

  // ===== Drawer Open/Close =====
  let drawerOpenedAt = 0;
  function getDrawerEl() {
    return document.getElementById('CTDRoot');
  }
  function openDrawer() {
    const d = getDrawerEl();
    if (!d) return;
    d.classList.add('is-open');
    d.setAttribute('aria-hidden', 'false');
    document.body.classList.add('ctd-cart-open');
    drawerOpenedAt = Date.now();
    // Cart bei jedem Open frisch ziehen (User könnte auf anderer Seite was geändert haben).
    // Wichtig nach Add-to-Cart (Dawn ruft cart-drawer.renderContents() auf, wir leiten das hierhin um).
    dbg('openDrawer → fetchCart');
    fetchCart();
  }
  function closeDrawer() {
    const d = getDrawerEl();
    if (!d) return;
    d.classList.remove('is-open');
    d.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('ctd-cart-open');
  }
  function isDrawerOpen() {
    const d = getDrawerEl();
    return d && d.classList.contains('is-open');
  }

  // ===== Add-to-Cart =====
  async function handleAddSubmit(e) {
    e.preventDefault();
    const form = e.currentTarget;
    const btn = form.querySelector('.ct-btn--add');
    const original = btn ? btn.textContent : '';
    if (btn) { btn.disabled = true; btn.textContent = 'Lege rein …'; }
    try {
      const formData = new FormData(form);
      const r = await fetch('/cart/add.js', { method: 'POST', body: formData, credentials: 'same-origin' });
      if (!r.ok) throw new Error('Add fehlgeschlagen');
      if (btn) btn.textContent = '✓ Hinzugefügt';
      setTimeout(() => { if (btn) { btn.disabled = false; btn.textContent = original; } }, 1200);
      await fetchCart();
      openDrawer();
    } catch (err) {
      console.error('CTD add error:', err);
      if (btn) {
        btn.textContent = 'Fehler — nochmal?';
        setTimeout(() => { btn.disabled = false; btn.textContent = original; }, 2000);
      }
    }
  }

  // ===== Quantity Change =====
  async function changeQty(line, newQty) {
    try {
      const r = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line: line, quantity: newQty }),
        credentials: 'same-origin'
      });
      if (!r.ok) throw new Error('Change failed');
      await fetchCart();
    } catch (err) {
      console.error('CTD qty change error:', err);
    }
  }

  async function handleQtyClick(e) {
    const btn = e.currentTarget;
    const action = btn.dataset.action;
    const line = parseInt(btn.dataset.line || btn.closest('.ctd-qty-pill').dataset.line, 10);
    if (!cart || !line) return;
    const item = cart.items[line - 1];
    if (!item) return;
    let newQty = item.quantity;
    if (action === 'plus') newQty++;
    else if (action === 'minus') newQty = Math.max(0, newQty - 1);
    else if (action === 'remove') newQty = 0;
    await changeQty(line, newQty);
  }

  // Direkte Mengen-Eingabe — debounced, damit jeder Tastendruck nicht den Cart fetcht
  function handleQtyInput(e) {
    const input = e.currentTarget;
    clearTimeout(input._ctdTimer);
    input._ctdTimer = setTimeout(() => commitQtyInput(input), 700);
  }

  function handleQtyBlur(e) {
    const input = e.currentTarget;
    clearTimeout(input._ctdTimer);
    commitQtyInput(input);
  }

  async function commitQtyInput(input) {
    const pill = input.closest('.ctd-qty-pill');
    if (!pill || !cart) return;
    const line = parseInt(pill.dataset.line, 10);
    if (!line) return;
    const item = cart.items[line - 1];
    if (!item) return;
    let newQty = parseInt(input.value, 10);
    if (isNaN(newQty) || newQty < 0) {
      // Ungültiger Wert → auf aktuelle Menge zurücksetzen
      input.value = item.quantity;
      return;
    }
    if (newQty === item.quantity) return; // keine Änderung
    await changeQty(line, newQty);
  }

  // ===== Cart-Clear =====
  async function handleClearCart() {
    try {
      await fetch('/cart/clear.js', { method: 'POST', credentials: 'same-origin' });
      selectedDate = null;
      await fetchCart();
    } catch (err) {
      console.error('CTD clear error:', err);
    }
  }

  // ===== Zapiet-Listener =====
  function checkZapietInput() {
    const input = document.querySelector('#Zapiet-InputCalendar__shippingDate_ISO, input[name="DeliveryDate"], input[name="Shipping-Date"], input[name="Pickup-Date"]');
    if (input && input.value) {
      const m = input.value.match(/(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
      if (m) {
        const d = new Date(parseInt(m[1]), parseInt(m[2]) - 1, parseInt(m[3]));
        if (!isNaN(d.getTime())) {
          const changed = !selectedDate || selectedDate.getTime() !== d.getTime();
          if (changed) {
            selectedDate = d;
            renderShipInfo();
          }
        }
      }
    }
  }

  function setupZapietObserver() {
    const mount = document.getElementById('storePickupApp');
    if (!mount) return;
    if (zapietObserver) zapietObserver.disconnect();
    zapietObserver = new MutationObserver(() => checkZapietInput());
    zapietObserver.observe(mount, { childList: true, subtree: true, attributes: true });
    checkZapietInput();
    // Re-Check nach 1s und 3s, falls Zapiet asynchron lädt
    setTimeout(checkZapietInput, 1000);
    setTimeout(checkZapietInput, 3000);
  }

  // ===== Init =====
  function init() {
    // Close-Button + Overlay-Klick verbinden
    const closeBtn = document.getElementById('CTDCloseBtn');
    if (closeBtn) closeBtn.addEventListener('click', closeDrawer);
    const overlay = document.getElementById('CTDOverlay');
    if (overlay) overlay.addEventListener('click', closeDrawer);

    // Dawn's cart-drawer Custom-Element open()/close() überschreiben.
    // Klicks auf den globalen Header-Cart-Icon rufen drawer.open() auf — wir leiten
    // das auf unseren eigenen Drawer um. Mit kurzem Retry-Loop, weil Dawn das Custom-
    // Element evtl. erst nach DOMContentLoaded definiert.
    const overrideDawnDrawer = () => {
      const dawn = document.querySelector('cart-drawer.ctd-shell');
      if (!dawn) return false;
      dawn.open = function () { openDrawer(); };
      dawn.close = function () { closeDrawer(); };
      // Dawn's product-form.js ruft cart.renderContents(parsedState) nach Add-to-Cart.
      // Original-Renderer würde .drawer__inner/#CartDrawer erwarten (existiert hier nicht).
      // Wir leiten das auf unseren openDrawer (der intern fetchCart triggert).
      dawn.renderContents = function (parsedState) {
        dbg('renderContents (Add-to-Cart hook) →', parsedState && parsedState.item_count, 'items');
        openDrawer();
      };
      dbg('Dawn-Override gebunden: open/close/renderContents');
      return true;
    };
    if (!overrideDawnDrawer()) {
      let tries = 0;
      const iv = setInterval(() => {
        tries++;
        if (overrideDawnDrawer() || tries > 20) clearInterval(iv);
      }, 100);
    }

    // Add-Forms (auf der Cart-Test-Page)
    document.querySelectorAll('.ct-add-form').forEach(f => f.addEventListener('submit', handleAddSubmit));

    // Helper-Buttons
    const clear = document.getElementById('ct-clear-cart');
    if (clear) clear.addEventListener('click', handleClearCart);
    const refresh = document.getElementById('ct-refresh-cart');
    if (refresh) refresh.addEventListener('click', () => fetchCart());
    const openBtn = document.getElementById('ct-open-cart');
    if (openBtn) openBtn.addEventListener('click', openDrawer);

    // Escape: erst Tooltip, dann Drawer
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape') {
        if (isTooltipOpen()) closeTooltip();
        else closeDrawer();
      }
    });

    // Trust-Bar Icons: Klick öffnet Info-Tooltip
    document.querySelectorAll('.ctd-trust--slim .ctd-trust__item[data-tooltip]').forEach(item => {
      item.addEventListener('click', e => {
        e.stopPropagation(); // Outside-Click-Handler nicht auslösen
        openTooltip(item.getAttribute('data-tooltip'));
      });
    });
    const tooltipBackdrop = document.getElementById('CTD-TooltipBackdrop');
    if (tooltipBackdrop) tooltipBackdrop.addEventListener('click', e => {
      e.stopPropagation();
      closeTooltip();
    });
    const tooltipClose = document.getElementById('CTD-TooltipClose');
    if (tooltipClose) tooltipClose.addEventListener('click', e => {
      e.stopPropagation();
      closeTooltip();
    });

    // Pickup-Mode-Watcher: MutationObserver auf #storePickupApp statt 1500ms-Polling.
    // Reagiert direkt wenn Zapiet die .active-Klassen auf den Method-Buttons umsetzt.
    function setupPickupWatcher() {
      const mount = document.getElementById('storePickupApp');
      if (!mount) {
        // Mount noch nicht gerendert — in 500ms erneut versuchen (max 20 Versuche)
        if (!setupPickupWatcher._tries) setupPickupWatcher._tries = 0;
        if (++setupPickupWatcher._tries < 20) setTimeout(setupPickupWatcher, 500);
        return;
      }
      window.__CTDLastPickupCheck = isPickupMode();
      const obs = new MutationObserver(() => {
        const now = isPickupMode();
        if (window.__CTDLastPickupCheck !== now) {
          window.__CTDLastPickupCheck = now;
          dbg('Pickup-Status hat sich geändert auf:', now);
          renderShippingHint();
          renderFreeShipping();
          renderShipInfo(); // Wording-Anpassung Versand/Abholung
        }
      });
      obs.observe(mount, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'data-method', 'aria-pressed']
      });
      // Bei Klicks im Mount zusätzlich Cart neu holen (für Hidden-Date-Updates)
      mount.addEventListener('click', () => setTimeout(() => fetchCart(), 300));
    }
    setupPickupWatcher();

    // Outside-Klick (außerhalb des Panels) schließt Drawer.
    // Skippt den allerersten Klick (200ms), damit der Open-Trigger nicht sofort wieder zumacht.
    document.addEventListener('click', e => {
      if (!isDrawerOpen()) return;
      if (Date.now() - drawerOpenedAt < 250) return;
      const panel = document.querySelector('.ctd-panel');
      if (!panel) return;
      if (!panel.contains(e.target)) closeDrawer();
    }, true);

    // Kommentar-Speicherung (debounced)
    const noteEl = document.getElementById('CTD-Note');
    const noteStatus = document.getElementById('CTD-NoteStatus');
    if (noteEl) {
      let noteTimer = null;
      noteEl.addEventListener('input', () => {
        if (noteStatus) { noteStatus.textContent = '…'; noteStatus.classList.remove('saved'); }
        clearTimeout(noteTimer);
        noteTimer = setTimeout(async () => {
          try {
            await fetch('/cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ note: noteEl.value }),
              credentials: 'same-origin'
            });
            if (noteStatus) { noteStatus.textContent = '✓ gespeichert'; noteStatus.classList.add('saved'); }
            setTimeout(() => { if (noteStatus) noteStatus.textContent = ''; }, 1800);
          } catch (err) {
            console.error('CTD note save error:', err);
            if (noteStatus) { noteStatus.textContent = 'Fehler beim Speichern'; }
          }
        }, 600);
      });
    }

    // Initialer Cart-Fetch
    fetchCart();

    // Zapiet beobachten (für selectedDate aus Hidden Input)
    setupZapietObserver();

    // Zapiet's verfügbare Tage aus DOM lesen (Source of Truth)
    watchZapietCalendar();

    // Globaler Change-Listener — falls Zapiet ein normales change-Event feuert
    document.addEventListener('change', e => {
      if (e.target && (e.target.id === 'Zapiet-InputCalendar__shippingDate_ISO' ||
                       e.target.name === 'DeliveryDate' ||
                       e.target.name === 'Shipping-Date' ||
                       e.target.name === 'Pickup-Date')) {
        checkZapietInput();
      }
    });

    // /cart-Redirect-Hook (2026-06-03): kommt der Besucher von der neutralisierten
    // Stand-alone-Cart-Seite, öffnen wir hier auf der Zielseite den Drawer.
    try {
      if (sessionStorage.getItem('reic:open-cart')) {
        sessionStorage.removeItem('reic:open-cart');
        openDrawer();
      }
    } catch (e) {}
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
